/**
 * V2 Carousel Section Configuration
 *
 * Maps V2 StepNode.content.carousel paths for the Carousel screen type.
 *
 * V2 CarouselConfig:
 * - kind: CarouselKind
 * - timer: { duration: number, unit?: CountdownUnit, action: Trigger }
 * - items: Array<Slide> where Slide = { progressStart, progressEnd, content: SlideContent }
 * - appearance: CarouselStyle
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { SliderItemVisibility } from '../types/sliderSection.types';
import type { ListStyleSectionConfig } from '../types/listSection.types';
import type { FieldConfig } from '../types/editorFieldTypes';
import { NUMBER_0_100_OPTIONS } from './atomicFields';
import { createTextStyleFields, remapTextFieldsForV2 } from './atomicFields';

/**
 * Valid CarouselKind values from V2 schema.
 * Must match CarouselKind enum: visual, visualCaption, visualCaptionDetail, visualCaptionDetailExtra
 */
export const V2_CAROUSEL_KIND_OPTIONS = [
  { value: 'visual', label: 'Image Only' },
  { value: 'visualCaption', label: 'Image + Title' },
  { value: 'visualCaptionDetail', label: 'Image + Title + Subtitle' },
  { value: 'visualCaptionDetailExtra', label: 'Image + Title + 2 Subtitles' },
];

export const DEFAULT_TIMER_DURATION = 10;

/**
 * Default new slide structure for V2 carousel
 */
export function createDefaultV2Slide(locale: string, progressStart: number, progressEnd: number) {
  return {
    progressStart,
    progressEnd,
    content: {
      title: {
        translations: { [locale]: '' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {},
      },
      subtitle: {
        translations: { [locale]: '' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {},
      },
      detail: {
        translations: { [locale]: '' },
        kind: 'static',
        templateParams: { labels: {}, links: {}, countdowns: {} },
        appearance: {},
      },
      image: { translations: {} },
    },
  };
}

/**
 * Convert progressStart/progressEnd array to duration percentages
 */
export function slidesToDurations(items: Array<{ progressStart: number; progressEnd: number }>): number[] {
  return items.map((item) => (item.progressEnd ?? 0) - (item.progressStart ?? 0));
}

/**
 * Convert duration percentages back to progressStart/progressEnd
 */
export function durationsToSlideRanges(durations: number[]): Array<{ progressStart: number; progressEnd: number }> {
  const ranges: Array<{ progressStart: number; progressEnd: number }> = [];
  let cumulative = 0;
  for (const d of durations) {
    ranges.push({ progressStart: cumulative, progressEnd: cumulative + d });
    cumulative += d;
  }
  return ranges;
}

/**
 * Get visibility configuration for carousel kind.
 * Controls which fields are shown in SliderItemEditor.
 * Pattern copied from sliderSectionConfig.ts getSliderVisibility().
 */
export function getCarouselVisibility(kind: string): SliderItemVisibility {
  return {
    showImage: true, // Always visible for carousel
    showTitle: kind !== 'visual',
    showSubtitle1: kind === 'visualCaptionDetail' || kind === 'visualCaptionDetailExtra',
    showSubtitle2: kind === 'visualCaptionDetailExtra',
  };
}

// ── Container options ────────────────────────────────────────────────

const VERTICAL_ALIGNMENT_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'bottom', label: 'Bottom' },
] as const;

const HEIGHT_KIND_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'fixed', label: 'Fixed (px)' },
  { value: 'percent', label: 'Percent (%)' },
] as const;

const NUMBER_0_1000_OPTIONS = Array.from({ length: 101 }, (_, i) => ({
  value: String(i * 10),
  label: String(i * 10),
}));

// ── Image / Text Container field sections ────────────────────────────

function createContainerSection(
  id: string,
  title: string,
  basePath: string,
): ListStyleSectionConfig {
  return {
    id,
    title,
    defaultExpanded: false,
    fields: [
      {
        path: `${basePath}.verticalAlignment`,
        label: 'Vertical Align',
        type: 'select',
        options: [...VERTICAL_ALIGNMENT_OPTIONS],
        defaultValue: 'top',
        isNullable: true,
      },
      {
        path: `${basePath}.heightKind`,
        label: 'Height Mode',
        type: 'select',
        options: [...HEIGHT_KIND_OPTIONS],
        defaultValue: 'percent',
        isNullable: true,
      },
      {
        path: `${basePath}.heightValue`,
        label: 'Height Value',
        type: 'numberCombo',
        options: NUMBER_0_100_OPTIONS,
        defaultValue: 50,
        defaultScrollValue: 50,
        isNullable: true,
      },
    ],
    paddingConfig: {
      basePath: `${basePath}.insets`,
      label: 'Container Padding',
    },
  };
}

/**
 * Creates container layout sections for the carousel editor.
 * imageContainer controls the image layer, textContainer controls the text layer.
 */
export function createV2ContainerSections(): ListStyleSectionConfig[] {
  return [
    createContainerSection(
      'imageContainerSettings',
      'Image Container',
      'content.carousel.imageContainer',
    ),
    createContainerSection(
      'textContainerSettings',
      'Text Container',
      'content.carousel.textContainer',
    ),
  ];
}

// ── Progress Indicator config (mirrors V2_PROGRESS_INDICATOR_CONFIG from v2HeaderBarConfig) ──

export const V2_CAROUSEL_PROGRESS_INDICATOR_CONFIG: FieldConfig = {
  path: 'content.carousel.progressIndicator',
  label: 'Page Indicator',
  type: 'pageIndicator',
  min: 0,
  max: 100,
  step: 1,
  defaultValue: 50,
  disableBehavior: 'null',

  indicatorKindPath: 'content.carousel.progressIndicator.kind',
  dashesConfigPath: 'content.carousel.progressIndicator',

  styleFields: [
    {
      path: 'content.carousel.progressIndicator.activeColor',
      label: 'page-indicator-color',
      type: 'color',
      defaultValue: '#ffc201',
      isNullable: false,
    },
    {
      path: 'content.carousel.progressIndicator.trackColor',
      label: 'page-indicator-track',
      type: 'color',
      defaultValue: '#ffffff',
      isNullable: false,
    },
  ],

  dashesStyleFields: [
    {
      path: 'content.carousel.progressIndicator.activeColor',
      label: 'filled color',
      type: 'color',
      defaultValue: '#12777f',
      isNullable: false,
    },
    {
      path: 'content.carousel.progressIndicator.trackColor',
      label: 'not filled color',
      type: 'color',
      defaultValue: '#f2f2f2',
      isNullable: false,
    },
  ],

  titleConfig: {
    path: 'content.carousel.progressIndicator.title',
    label: 'title',
    type: 'text',
    l10nPath: 'content.carousel.progressIndicator.title',
    defaultValue: 'Progress',
    styleFields: remapTextFieldsForV2(createTextStyleFields('content.carousel.progressIndicator.title')),
  },
};
